import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppSelector, useAppDispatch } from '../../store';
import UserSlice from '../../store/slices/User';

// COMPONENTS
import Button from '../../components/Button';

// IMAGES
import WelcomeImage from '../../assets/img/welcome-background.png';

import DashboardImageLeader from '../../assets/img/welcome-dashboard-leader.png';
import DashboardImageStaff from '../../assets/img/welcome-dashboard-staff.png';
import DashboardImageFamilyVisitor from '../../assets/img/welcome-dashboard-family-visitor.png';

import sideMenuImageLeader from '../../assets/img/welcome-side-menu-leader.png';
import sideMenuImageStandard from '../../assets/img/welcome-side-menu-standard.png';

import ActivityImage from '../../assets/img/welcome-activity-feed.png';
import SwitchOrganisationImage from '../../assets/img/welcome-switch-org.png';
import CompleteImage from '../../assets/img/welcome-complete.png';

// ICONS
import { ReactComponent as LeftArrrow } from '../../assets/svg/chevron-left-purple.svg';
import { ReactComponent as RightArrrow } from '../../assets/svg/chevron-right-purple.svg';
import { ReactComponent as ActiveDot } from '../../assets/svg/purple-dot.svg';
import { ReactComponent as DeactiveDot } from '../../assets/svg/grey-dot.svg';

// TYPES
import { DomainProfileType } from '../../api/models';

interface WelcomeModalTypes {
	isOpen: boolean;
	userName: string;
	isOrgAdmin: boolean;
	contactEmail?: string;
}

const WelcomeModal: React.FC<WelcomeModalTypes> = ({ isOpen, userName, isOrgAdmin, contactEmail }) => {
	const dispatch = useAppDispatch();

	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Check Profile Type of the User
	const activeProfileType = useAppSelector((state) => state.user.activeProfileType);

	// Check if User is Org Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	const [open, setOpen] = React.useState<boolean>(isOpen);

	const [currentStep, setCurrentStep] = React.useState<number>(1);

	const previousStep = () => {
		if (currentStep <= 6 && currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};
	const nextStep = () => {
		if (currentStep < 6 && currentStep > 1) {
			setCurrentStep(currentStep + 1);
		}
	};

	const sideImageDisplay = () => {
		switch (currentStep) {
			case 2:
				if (isOrganisationAdmin === true) {
					return <img alt="welcome-image" src={DashboardImageLeader} />;
				} else if (isOrganisationAdmin === false && activeProfileType === DomainProfileType.Staff) {
					return <img alt="welcome-image" src={DashboardImageStaff} />;
				} else {
					return <img alt="welcome-image" src={DashboardImageFamilyVisitor} />;
				}
			case 3:
				if (isOrganisationAdmin === true) {
					return <img alt="welcome-image" src={sideMenuImageLeader} />;
				} else {
					return <img alt="welcome-image" src={sideMenuImageStandard} />;
				}
			case 4:
				return <img alt="welcome-image" src={ActivityImage} />;
			case 5:
				return <img alt="welcome-image" src={SwitchOrganisationImage} />;
			case 6:
				return <img alt="welcome-image" src={CompleteImage} />;
			default:
				return <img alt="welcome-image" src={WelcomeImage} />;
		}
	};

	const closeWelcomeModal = async () => {
		await apibridge.postAccountWalkthroughComplete();

		setOpen(false);

		dispatch(UserSlice.actions.update({ isWalkThroughCompleted: true }));
	};

	return (
		<>
			{open ? (
				<>
					<div className="welcome-modal ">
						<div className="modal-content row">
							{currentStep !== 1 ? (
								<>
									<div className="col-md-6 text-left">
										{currentStep === 2 ? (
											<div>
												<h1>Dashboard</h1>

												<p className="sub-grey-text">
													The Dashboard is your home screen. Explore the functions of ChildAware in the Side Menu.
													Actions for your attention will be highlighted in the middle section of the Dashboard.
												</p>

												<p className="sub-grey-text">
													Return to the Dashboard at any time by clicking on ‘Dashboard’ in the Side Menu.
												</p>
											</div>
										) : currentStep === 3 ? (
											<div>
												<h1>Side menu</h1>

												<p className="sub-grey-text">
													The Side Menu lists the functions of ChildAware. Click on a function and follow the prompts.
												</p>

												<p className="sub-grey-text">
													If you need help you can ask within your organisation or you can{' '}
													<a href={`${websiteUrl}/contact-us`} target="_blank">
														contact us
													</a>
													.
												</p>
											</div>
										) : currentStep === 4 ? (
											<div>
												<h1>Activity feed</h1>

												<p className="sub-grey-text">
													The Activity Feed lists activities undertaken by an organisation to make it a safe place for
													children.
												</p>

												<p className="sub-grey-text">
													Organisations can add an activity at any time and users can view details of these activities.
												</p>
											</div>
										) : currentStep === 5 ? (
											<div>
												<h1>Switching organisations</h1>

												<p className="sub-grey-text">
													You can use the toggle at the top of the Side Menu to switch between organisations. For
													example, you may work at a school, volunteer at a sporting club and be a parent of a child
													attending childcare. You can link your ChildAware account to all of these organisations and
													switch between these organisations.
												</p>
											</div>
										) : (
											<div className="close">
												<h1>Thanks!</h1>

												<p className="sub-grey-text">
													Thanks for using ChildAware to keep children safe. Remember that everything you need is on the
													Dashboard. If you need help you can ask within your organisation or you can{' '}
													<a href={`${websiteUrl}/contact-us`} target="_blank">
														contact us
													</a>
													.
												</p>

												<Button onClick={() => closeWelcomeModal()} className="btn-primary full-width">
													Close tutorial
												</Button>
											</div>
										)}

										<div className="swiper-controls">
											<Button className="btn-icon welcome-modal-control" onClick={() => previousStep()}>
												<LeftArrrow />
											</Button>

											<div className="swiper-dots">
												{currentStep === 2 ? <ActiveDot /> : <DeactiveDot />}
												{currentStep === 3 ? <ActiveDot /> : <DeactiveDot />}
												{currentStep === 4 ? <ActiveDot /> : <DeactiveDot />}
												{currentStep === 5 ? <ActiveDot /> : <DeactiveDot />}
											</div>

											<Button
												className={`btn-icon welcome-modal-control ${currentStep === 6 ? 'complete' : null}`}
												onClick={() => nextStep()}
											>
												<RightArrrow />
											</Button>
										</div>
									</div>

									<div className="d-none d-md-flex col-md-6 image-right">{sideImageDisplay()}</div>
								</>
							) : (
								<>
									<div className="col-md-6 text-left">
										<div>
											<h1 className="title">Welcome {userName}!</h1>

											<p className="sub-grey-text">Let’s start with an overview of the main features of ChildAware.</p>

											{isOrganisationAdmin && (
												<>
													<p className="sub-grey-text">
														As a leader of your organisation, you have oversight of your organisation’s ChildAware
														portal.
													</p>

													<p className="sub-grey-text">
														By clicking on Help Desk in the Side Bar you will find simple information explaining how to
														use each part of the portal.
													</p>

													<p className="sub-grey-text">
														If you can’t find what you are looking for or need more help, please{' '}
														<a href={`${websiteUrl}/contact-us`} target="_blank">
															contact us
														</a>
														.
													</p>
												</>
											)}
										</div>

										<Button onClick={() => setCurrentStep(2)} className="btn-primary full-width">
											Let's go
										</Button>
									</div>

									<div className="d-none d-md-flex col-md-6 image-right">
										<img alt="" src={WelcomeImage} />
									</div>
								</>
							)}
						</div>
					</div>
				</>
			) : null}
		</>
	);
};

export default WelcomeModal;
